import { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useSystemStateHooks } from '../../../modules/system'
import { UIPermission } from '../state/types'
import { RoutePath } from '../../routes/types'

export interface GlobalShortcut {
    key: string
    label: string
    permission: UIPermission
    module: RoutePath
}

export const GLOBAL_SHORTCUTS: GlobalShortcut[] = [
    { key: 'V', label: 'Nueva Venta', permission: UIPermission.SAVE_SALES, module: RoutePath.SALES },
    { key: 'P', label: 'Nuevo Presupuesto', permission: UIPermission.SAVE_BUDGETS, module: RoutePath.BUDGETS },
    { key: 'A', label: 'Nuevo Acopio', permission: UIPermission.SAVE_HOARDS, module: RoutePath.HOARDS },
    { key: 'C', label: 'Nueva Compra', permission: UIPermission.SAVE_PURCHASES, module: RoutePath.PURCHASES },
    { key: 'H', label: 'Nueva Hoja de Ruta', permission: UIPermission.SAVE_ROADMAPS, module: RoutePath.ROADMAPS },
]

// Derivados de GLOBAL_SHORTCUTS: agregar un shortcut nuevo solo requiere editar el array de arriba
const CONTEXTUAL_MODULES = new Set(GLOBAL_SHORTCUTS.map(s => s.module))
const MODULE_PERMISSIONS: Partial<Record<RoutePath, UIPermission>> = Object.fromEntries(
    GLOBAL_SHORTCUTS.map(s => [s.module, s.permission])
)

const isEditableFocused = (target: EventTarget | null): boolean => {
    if (!target || !(target instanceof HTMLElement)) return false
    return (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target.isContentEditable
    )
}

const getContextualModule = (pathname: string): RoutePath | undefined => {
    const parts = pathname.split('/').filter(Boolean)
    // Only on list pages: /:companyCode/:module (exactly 2 segments)
    if (parts.length !== 2) return undefined
    const module = parts[1] as RoutePath
    return CONTEXTUAL_MODULES.has(module) ? module : undefined
}

export const useKeyboardShortcuts = () => {
    const [isCheatsheetOpen, setIsCheatsheetOpen] = useState(false)
    const { pathname } = useLocation()
    const { companyCode, userHasPermission } = useSystemStateHooks()

    const closeCheatsheet = useCallback(() => setIsCheatsheetOpen(false), [])

    const buildCreateUrl = useCallback((module: RoutePath): string => {
        return `/${companyCode}/${module}/${RoutePath.CREATE}`
    }, [companyCode])

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            setIsCheatsheetOpen(false)
            return
        }

        if (!event.ctrlKey || !event.altKey) return
        if (isEditableFocused(event.target)) return

        if (event.key === ' ') {
            event.preventDefault()
            setIsCheatsheetOpen(prev => !prev)
            return
        }

        if (!companyCode) return

        const key = event.key.toLowerCase()

        if (key === 'n') {
            event.preventDefault()
            const module = getContextualModule(pathname)
            if (!module) return
            const permission = MODULE_PERMISSIONS[module]
            if (permission && !userHasPermission(permission)) return
            window.open(buildCreateUrl(module), '_blank')
            return
        }

        const shortcut = GLOBAL_SHORTCUTS.find(s => s.key.toLowerCase() === key)
        if (!shortcut) return

        event.preventDefault()
        if (!userHasPermission(shortcut.permission)) return
        window.open(buildCreateUrl(shortcut.module), '_blank')
    }, [pathname, companyCode, userHasPermission, buildCreateUrl])

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [handleKeyDown])

    return {
        isCheatsheetOpen,
        closeCheatsheet,
        contextualModule: getContextualModule(pathname),
    }
}
